import { Injectable, OnDestroy, Optional } from "@angular/core";
import { cloneDeep, isEqual, omit } from "lodash-es";
import { TableState } from "primeng/api";
import { ReplaySubject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { TableColumnConfiguration, TableConfiguration, TableConfigurationSettings } from '.';
import { TableSettingsService } from "./avano-table-settings.service";
import { AvanoTableComponent, CustomizedTable } from "./avano-table.component";

const primeNgTableStorages = {
  localStorage: 'local',
  sessionstorage: 'session'
}

@Injectable({providedIn: 'any'})
export class AvanoTableStateService implements OnDestroy {
  private destroyed$ = new ReplaySubject(1);

  private debounceSaveTime: number;
  private tableIdentifier: string;
  private tableSettings: TableConfigurationSettings;
  private lastSavedSettings: TableState;
  private allColumns: TableColumnConfiguration[];

  private tableRef: CustomizedTable;

  constructor(@Optional() public settingsService: TableSettingsService) {
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public init(avanoTable: AvanoTableComponent) {
    if (!avanoTable.tableRef) {
      console.warn("Bad initialization of AvanoTableStateService. TableRef needs to be set!")
      return;
    }
    this.tableRef = avanoTable.tableRef;
    this.tableSettings = avanoTable.tableConfiguration?.tableSettings;
    this.tableIdentifier = avanoTable.tableConfiguration?.tableSettings?.settingsDbIdentifier;
    this.allColumns = avanoTable.allColumns;
    if (this.settingsService && this.tableIdentifier) {
      this.debounceSaveTime = this.settingsService.saveDebounceTimeInMs;
      this.setupSettingsSaveRestore();
    }
  }

  private setupSettingsSaveRestore(): void {
    this.enablePrimeNGTableStatefulness()

    this.extendRestoreStateMechanism();

    this.attachOnStateSaveListenerToTable();

    this.tableRef.restoreState();
  }

  private enablePrimeNGTableStatefulness() {
      this.tableRef.stateStorage = primeNgTableStorages.sessionstorage;
      this.tableRef.stateKey = this.tableIdentifier;
  }


  private attachOnStateSaveListenerToTable() {
    this.tableRef.onStateSave
      .pipe(
        debounceTime(this.debounceSaveTime),
        takeUntil(this.destroyed$)
      )
      .subscribe((state: TableState) => this.handleOnStateSaveEvent(state));
  }

  private handleOnStateSaveEvent(tableState: TableState): void {
    let convertedState: TableState;
    const tableStateExists = tableState && Object.keys(tableState).length > 0;
    if (tableStateExists) {
      convertedState = {
        columnOrder: [...tableState.columnOrder],
        columnWidths: tableState.columnWidths,
        tableWidth: tableState.tableWidth
      }

      if (this.tableSettings.settingsKeepFilters) {
        convertedState.filters = tableState.filters ?? {};
      }

      if (this.tableSettings.settingsKeepRows) {
        convertedState.rows = tableState.rows;
      }

      convertedState.columnOrder.forEach((columnField: string, columnIndex: number) => {
        const isUserHiddenColumn = this.allColumns.find(originalColumn => originalColumn.field === columnField)?.userHidden === true;
        if (isUserHiddenColumn) {
          convertedState.columnOrder.splice(columnIndex, 1);
          convertedState.columnWidths  = convertedState.columnWidths
            .split(',')
            .splice(columnIndex, 1)
            .join(',');
        }
      });
    } else {
      // We reset to the default settings
      convertedState = {};
    }

    this.overwritePrimeNgLocalStorage(convertedState);

    // omit some keys before compare.
    const filteredConvertedState = omit(convertedState, 'tableWidth', 'columnWidths');
    const filteredLastSavedSettings = omit(this.lastSavedSettings, 'tableWidth', 'columnWidths');
    if (!isEqual(filteredConvertedState, filteredLastSavedSettings)) {
      const settingsIdentifier = this.tableIdentifier;
      this.settingsService.saveSettings(settingsIdentifier, convertedState).subscribe((saved) => {
        if (!saved) {
          console.warn("Saving settings failed");
        } else {
          this.lastSavedSettings = cloneDeep(convertedState);
        }
      });
    }
  }

  private overwritePrimeNgLocalStorage(convertedState: TableState) {
    const settingsIdentifier = this.tableIdentifier;
    const storage = this.tableRef.getStorage();
    storage.setItem(settingsIdentifier, JSON.stringify(convertedState));
  }

  private removeNoLongerExistingColumns(settings: TableState): void {
    settings.columnOrder?.forEach((columnField: string, currentPosition: number) => {
      const columnNoLongerExists = !this.tableRef.findColumnByKey(columnField);
      if (columnNoLongerExists) {
        settings.columnOrder.splice(currentPosition, 1);
        settings.columnWidths = settings.columnWidths.split(',').splice(currentPosition, 1).join(',');
      }
    });
  }

  private extendRestoreStateMechanism() {
    this.tableRef._defaultRestoreState = this.tableRef.restoreState;
    this.tableRef.restoreState = () => {
      (this.settingsService).getSettings(this.tableIdentifier).subscribe((settings: TableState) => {
        const settingsFound = settings && Object.keys(settings).length;
        if (settingsFound) {
          this.lastSavedSettings = cloneDeep(settings);

          this.removeNoLongerExistingColumns(settings);

          this.overwritePrimeNgLocalStorage(settings);
          this.tableRef.columnWidthsState = settings.columnWidths;
          this.tableRef.tableWidthState = settings.tableWidth;
          this.tableRef._defaultRestoreState();
          this.tableRef.restoreColumnOrder();

          setTimeout(() => this.tableRef.restoreColumnWidths(), 0);
          // This really needs to be called async, because DOM isn't fully rendered yet and therefore wrong <th> elements are "restored". Even with 0ms delay it's good enough
          // TODO: find a better initialization/hook to avoid having to comment it.

        } else {
          // Keep default settings
          // Tell primeng that restoring state worked, so that we do not create an endless loop for restoring
          this.tableRef.stateRestored = true;
        }

        // Lets activate the default lazyLoad events and trigger them.
        this.tableRef._customRestoreStateCalled = true;
        this.tableRef.onLazyLoad.emit(this.tableRef.createLazyLoadMetadata());
      });
    };
  }
}
